import { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppContext } from "../contexts/AppContext";
import { useSearchContext } from "../contexts/SearchContext";

const Pricing: React.FC = ({}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { destId, responseToken, hotel, totalCost } = location.state;
  const { isLoggedIn, user } = useAppContext();
  const { guests } = useSearchContext();
  const totalAdults = guests.reduce((sum, g) => sum + g.adults, 0);
  const totalChildren = guests.reduce((sum, g) => sum + g.children.length, 0);

  const totalCount = totalAdults + totalChildren;
  const depositPrice = (totalCost * 0.2 ).toFixed(2);
  const fullPayment = (totalCost / totalCount).toFixed(2);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleHoldHotel = async () => { 
    if (!isLoggedIn) {
      navigate("/sign-in", { state: { from: location.pathname } });
      return;
    }
    navigate("/booking-form",{
      state: {
        destId,
        responseToken,
        primaryGuestName: `${user?.firstName} ${user?.lastName}`,
        numAdults: totalAdults,
        numChilds: totalChildren,
        totalCost: totalCost,
        hotel: hotel,
        bookingType: 'hold'
      },
    });
  };

  const handlePayDeposit = async () => {
    navigate("/booking-form", {
      state: {
        destId,
        responseToken,
        primaryGuestName: `${user?.firstName} ${user?.lastName}`,
        numAdults: totalAdults,
        numChilds: totalChildren,
        totalCost: depositPrice,
        hotel: hotel,
        bookingType: 'pay'
      },
    });
  };

  const handleFullPayment = async () => {
    navigate("/booking-form", {
      state: {
        destId,
        responseToken,
        primaryGuestName: `${user?.firstName} ${user?.lastName}`,
        numAdults: totalAdults,
        numChilds: totalChildren,
        totalCost: fullPayment,
        hotel: hotel,
        bookingType: 'pay'
      },
    });
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Pricing | Halo Holidays</title>
          <meta name="description" content="Already a member? Sign in to your Halo Holidays account to manage your bookings, view your travel history, and access exclusive deals." />
        </Helmet>
      </HelmetProvider>
      <section className="w-full my-4 mt-16 px-2 md:px-0">
        <div className="flex justify-center bg-white">
          <div className="flex flex-col md:flex-row space-x-0 md:space-x-4 w-full">
            {/* Package Card 1 */}
            <div className="flex flex-col justify-between p-4 rounded-lg border border-gray-300 shadow-xl flex-grow relative bg-white mb-10 md:mb-0 h-96 hover:border-halo-pink">
              {/* <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-[#ff78b8] text-white text-sm font-semibold uppercase py-1 px-3 rounded-lg">
                Free Fare Hold
              </div> */}
              <h3 className="text-xl font-semibold mt-6">Free Fare Hold</h3>
              <div className="text-sm">
              <p className="text-black flex items-center">
                  <img src="/featureIcons/point-tick.svg" alt="No voucher icon" className="w-6 h-6 mr-1 -ml-1" />
                  96 Hour Free Fare Hold
                </p>
                <p className="text-black flex items-center">
                  <img src="/featureIcons/point-x.svg" alt="No discount icon" className="w-4 h-4 mr-2" />
                  No discount
                </p>
                <p className="text-black flex items-center">
                  <img src="/featureIcons/point-x.svg" alt="No voucher icon" className="w-4 h-4 mr-2" />
                  No voucher
                </p>                
              </div>
              <div className="text-center mt-4 -mb-4">
                <p className="text-black font-bold text-xl">Reserve now, pay later</p>
                <p>No payment, no rush, no stress</p>
              </div>
              <button onClick={handleHoldHotel} className="mt-4 border border-halo-pink text-halo-pink px-4 py-2 rounded-md font-medium hover:bg-halo-pink hover:text-white">Reserve</button>
            </div>

            {/* Package Card 2 - Best Value */}
            <div className="flex flex-col justify-between p-4 rounded-lg border border-gray-300 shadow-xl flex-grow relative bg-white mb-10 md:mb-0 h-96 hover:border-halo-pink">
            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-white text-sm font-semibold uppercase py-1 px-3 bg-gradient-to-r from-[#dc0069] to-[#116FFF] best-shadow">
                Best Value
              </div>
              <h3 className="text-xl font-semibold mt-6">Easy Payment Plan</h3>
              <div className="text-sm">                
                <p className="text-black flex items-center">
                  <img src="/featureIcons/point-tick.svg" alt="No voucher icon" className="w-6 h-6 mr-1 -ml-1" />
                  $75 Voucher for your next trip
                </p>
                <p className="text-black flex items-center">
                  <img src="/featureIcons/point-tick.svg" alt="No voucher icon" className="w-6 h-6 mr-1 -ml-1" />
                  Flexible payment options
                </p>
                <p className="text-black flex items-center">
                  <img src="/featureIcons/point-x.svg" alt="No discount icon" className="w-4 h-4 mr-2" />
                  No discount
                </p>
              </div>
              <div className="text-center mt-4 -mb-4">
                <p className="text-black font-bold text-xl">$ {depositPrice}</p>
                <p>Or with a deposit of £53.2</p>
              </div>
              <button onClick={handlePayDeposit} className="mt-4 border border-halo-pink text-halo-pink px-4 py-2 rounded-md font-medium hover:bg-halo-pink hover:text-white">Select</button>
            </div>

            {/* Package Card 3 */}
            <div className="flex flex-col justify-between p-4 rounded-lg border border-gray-300 shadow-xl flex-grow relative bg-white mb-10 md:mb-0 h-96 hover:border-halo-pink">
              {/* <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-[#8a0142] text-white text-sm font-semibold uppercase py-1 px-3 rounded-lg">
                BIG Savings
              </div> */}
              <h3 className="text-xl font-semibold mt-6">BIG Savings</h3>
              <div className="text-sm">
                <p className="text-black flex items-center">
                  <img src="/featureIcons/point-tick.svg" alt="No voucher icon" className="w-6 h-6 mr-1 -ml-1" />
                  $275 additional discount
                </p>
                <div className="flex items-center">
                  <img src="/featureIcons/point-tick.svg" alt="Point tick icon" className="w-6 h-6 mr-1 -ml-1" />
                  <div>
                    <p className="text-black mb-0">$150 voucher for your next trip</p>
                    <p className="text-gray-500 -mt-2 text-sm">Minimum spend: $1800</p>
                  </div>
                </div>
                <p className="text-black flex items-center">
                  <img src="/featureIcons/point-x.svg" alt="No discount icon" className="w-4 h-4 mr-2" />
                  Flexible payment options
                </p>
              </div>
              <div className="text-center mt-4 -mb-4">
                <p className="text-black font-bold text-xl">$ {fullPayment}</p>
                <p>Pay in full only</p>
              </div>
              <button onClick={handleFullPayment} className="mt-4 border border-halo-pink text-halo-pink px-4 py-2 rounded-md font-medium hover:bg-halo-pink hover:text-white">Select</button>
            </div>
          </div>
        </div>
        {/* <div className="mt-10">
          <img src="/general/relax.png" alt="relax with confidence"
            className="rounded-md shadow-sm border-2 border-gray-100" />
        </div> */}
      </section>
    </>
  );
};

export default Pricing;
